import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Empty } from 'components/Empty';
import { Status } from 'components/Status';
import { TooltipIcon, TooltipWrapper } from 'components/Tooltip';
import { Text } from 'components/Typography';
import { Appearance, theme, toRem } from 'design';
import { routeForPaymentDetail } from 'routes/routeConstants';
import { IPaymentSet, PaymentWorkflowTypes, PayoutStatus } from 'types';
import { centsToDollarsDisplay, getTimeWithZone } from 'utils';
import { DEFAULT_PAGE_SIZE, isSortDesc, isSortedBy } from 'utils/table';
import { Cell } from './Cell';
import EmptyTable from './EmptyTable';
import HeaderCell from './HeaderCell';
import HeaderRow from './HeaderRow';
import Row from './Row';
import SortLabel from './SortLabel';
import Table from './Table';
import TableBody from './TableBody';
import TableHead from './TableHead';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const FixedHeightRow = styled(Row)`
  height: ${toRem(106)};
`;

const CellColumn = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};
  width: 100%;

  > * {
    margin-bottom: 0;
    color: ${theme.color.neutral[900]};
  }
`;

const TotalsLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
`;

// Labels in one column, amounts in the other.
const Totals = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: ${toRem(theme.space['2xs'])} ${toRem(theme.space.sm)};
  text-align: left;

  > * {
    color: ${theme.color.white};
  }
`;

const TooltipContent = styled.div`
  display: flex;
  flex-flow: column nowrap;

  > p {
    color: ${theme.color.white};
  }
`;

const PAYOUT_STATUS_DISPLAY_PROPS = [
  { type: PayoutStatus.PAID, appearance: Appearance.SUCCESS, label: 'Paid' },
  { type: PayoutStatus.PAYROLL, appearance: Appearance.PAYROLL, label: 'Payroll' },
  { type: PayoutStatus.CASH, appearance: Appearance.CASH, label: 'Cash' },
  { type: PayoutStatus.PENDING, appearance: Appearance.WARNING, label: 'Pending' },
  { type: PayoutStatus.EXPIRED, appearance: Appearance.DANGER, label: 'Expired' },
  { type: PayoutStatus.CANCELED, appearance: Appearance.NEUTRAL, label: 'Canceled' },
];

interface IPaymentOverviewTableProps {
  dataCy?: string;
  isLoading: boolean;
  onSortChange: (sort: string) => void;
  paymentSets?: IPaymentSet[];
  urlQuery: IApiQueryOptions;
}

const PaymentOverviewTable: React.FC<IPaymentOverviewTableProps> = ({ dataCy = 'payment-overview-table', isLoading, onSortChange, paymentSets, urlQuery }) => {
  const history = useHistory();
  const currentSort = urlQuery.sort || '';
  const sortDesc = isSortDesc(currentSort);

  return (
    <Layout data-cy={`${dataCy}-wrapper`}>
      <Table scrollable sticky cellMinWidth={100}>
        <TableHead>
          <HeaderRow sticky>
            <HeaderCell onClick={() => onSortChange('payment_date')} data-cy="header-payment-date">
              <SortLabel label="Business Day" isSorted={isSortedBy('payment_date', currentSort)} isSortedDesc={sortDesc} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell align="right" onClick={() => onSortChange('totals.net')} data-cy="header-totals">
              <SortLabel label="Net Paid" isSorted={isSortedBy('totals.net', currentSort)} isSortedDesc={sortDesc} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell data-cy="header-status">{isLoading ? '' : 'Status'}</HeaderCell>

            <HeaderCell onClick={() => onSortChange('created_by.name')} data-cy="header-sent-by">
              <SortLabel label="Sent By" isSorted={isSortedBy('created_by.name', currentSort)} isSortedDesc={sortDesc} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell data-cy="header-payment-set-id">{isLoading ? '' : 'Set ID'}</HeaderCell>

            <HeaderCell onClick={() => onSortChange('description')} data-cy="header-description">
              {isLoading ? '' : 'Description'}
            </HeaderCell>
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading
            ? [...Array(DEFAULT_PAGE_SIZE)].map((_, index) => (
                <FixedHeightRow key={index} data-cy={`${dataCy}-loading-row`}>
                  <Cell>
                    <CellColumn>
                      <Skeleton width="100%" />
                      <Skeleton width="75%" />
                    </CellColumn>
                  </Cell>
                  <Cell>
                    <Skeleton width="100%" />
                  </Cell>
                  <Cell>
                    <Skeleton width="100%" />
                  </Cell>
                  <Cell>
                    <Skeleton width="100%" />
                  </Cell>
                  <Cell>
                    <Skeleton width="100%" />
                  </Cell>
                  <Cell>
                    <Skeleton width="100%" />
                  </Cell>
                </FixedHeightRow>
              ))
            : paymentSets?.map((paymentSet: IPaymentSet) => {
                const totalPayees = paymentSet.totals?.payeeCount ?? 0;

                return (
                  <FixedHeightRow key={`payment-set-row-${paymentSet.id}`} clickable onClick={() => history.push(routeForPaymentDetail(paymentSet.id))} data-cy="payment-set-row" data-value={paymentSet.id}>
                    <Cell data-cy="business-day-cell">
                      <CellColumn>
                        <TooltipWrapper>
                          <Text size="small" fontWeight="semibold">
                            {paymentSet.paymentDate}
                          </Text>
                          <TooltipIcon iconMarginLeft={12} id={paymentSet.id}>
                            <TooltipContent>
                              <Text size="xxsmall" fontWeight="semibold" color={theme.color.white}>
                                Date Submitted
                              </Text>
                              <Text size="xxsmall" color={theme.color.white}>
                                {dayjs(paymentSet.createdAt).format('YYYY-MM-DD')}
                              </Text>
                              <Text size="xxsmall" color={theme.color.white}>
                                {getTimeWithZone(paymentSet.createdAt)}
                              </Text>
                            </TooltipContent>
                          </TooltipIcon>
                          {paymentSet.workflow === PaymentWorkflowTypes.CALCULATOR && (
                            <TooltipIcon iconMarginLeft={4} id={`workflow_${paymentSet.id}`} icon="CalculatorOutline">
                              Payment submitted using tip calculator
                            </TooltipIcon>
                          )}
                          {paymentSet.payPeriodClosed && (
                            <TooltipIcon iconMarginLeft={4} id={`pay_period_${paymentSet.id}`} icon="LockClosed" dataCy="closed-pay-period-icon">
                              Pay Period Closed
                            </TooltipIcon>
                          )}
                        </TooltipWrapper>

                        <Text size="small" data-cy="payment-set-total-payees">
                          {`${totalPayees} ${totalPayees === 1 ? 'Payee' : 'Payees'}`}
                        </Text>
                      </CellColumn>
                    </Cell>

                    <Cell align="right" data-cy="net-cell">
                      <TotalsLayout>
                        <Text size="small" fontWeight="semibold">
                          {centsToDollarsDisplay(paymentSet.totals?.net)}
                        </Text>
                        <TooltipIcon iconMarginLeft={theme.space.xs} id={`totals_${paymentSet.id}`}>
                          <Totals>
                            <Text size="xxsmall" fontWeight="semibold">
                              Gross:
                            </Text>
                            <Text size="xxsmall">{centsToDollarsDisplay(paymentSet.totals?.gross)}</Text>
                            <Text size="xxsmall" fontWeight="semibold">
                              Holdbacks:
                            </Text>
                            <Text size="xxsmall">{centsToDollarsDisplay(paymentSet.totals?.holdbacks)}</Text>
                            <Text size="xxsmall" fontWeight="semibold">
                              Net:
                            </Text>
                            <Text size="xxsmall">{centsToDollarsDisplay(paymentSet.totals?.net)}</Text>
                          </Totals>
                        </TooltipIcon>
                      </TotalsLayout>
                    </Cell>

                    <Cell data-cy="status-cell">
                      <CellColumn>
                        {PAYOUT_STATUS_DISPLAY_PROPS.map(({ type, appearance, label }) => {
                          const count = (paymentSet.totals?.status || []).find((s) => s.type === type)?.count;
                          if (!count) return null;
                          return <Status key={type} data-cy={`status-${type}`} appearance={appearance} label={`${label} (${count})`} />;
                        })}
                      </CellColumn>
                    </Cell>

                    <Cell data-cy="sent-by-cell">{paymentSet.createdBy?.name}</Cell>

                    <Cell data-cy="payment-set-id-cell">
                      <Text size="small" data-cy="payment-set-id">
                        {paymentSet.id}
                      </Text>
                    </Cell>

                    <Cell minWidth={300} maxWidth={350} wrapText data-cy="description-cell">
                      {paymentSet?.description || '--'}
                    </Cell>
                  </FixedHeightRow>
                );
              })}
          {!isLoading && isEmpty(paymentSets) && (
            <EmptyTable>
              <Empty icon="Payment" message="No payments found." />
            </EmptyTable>
          )}
        </TableBody>
      </Table>
    </Layout>
  );
};

export default PaymentOverviewTable;
